import { ChevronDownIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
  useDisclosure
} from '@chakra-ui/react'
import { MouseEvent, useEffect, useMemo, useState } from 'react'
import { FiChevronLeft, FiChevronRight, FiImage, FiMaximize2 } from 'react-icons/fi'

import { ActionButton } from '../../../components/ActionButton'
import { FormModal } from '../../../components/FormModal'
import ArtworkPreviewModal from '../../../components/previewModal/ArtworkPreviewModal'
import { FormSelect } from '../../../components/Select'
import { EXHIBITION_STATUS, StatusBadge } from '../../../components/StatusBadge'
import { Tooltip } from '../../../components/Tooltip'
import { useAppToast } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import {
  IArtEvaluationByEvaluator,
  IContestArtworkForEvaluation,
  IContestEvaluationCriterion,
  IContestEvaluator
} from '../../../services/Contests/interfaces'
import { resolveArtMeta } from '../Contests/artFieldHelpers'
import ArtMetaInline from '../Contests/ArtMetaInline'
import { calculateWeightedAverage } from './evaluationCriteria'

interface IArtworkEvaluationModalProps {
  isOpen: boolean
  onClose: () => void
  artwork: IContestArtworkForEvaluation | null
  evaluators: IContestEvaluator[]
  criteria: IContestEvaluationCriterion[]
  onSaved: (artId: number, updated: IContestArtworkForEvaluation) => void
  /** Optional navigation between artworks within the current listing (DEV-489). */
  onPrev?: () => void
  onNext?: () => void
  positionLabel?: string
  onStatusChanged?: (artId: number, newStatus: IContestArtworkForEvaluation['exhibition_status']) => void
  /** view_contest_votes: grades and status are shown but cannot be changed. */
  readOnly?: boolean
}

function avg(values: number[]): number | null {
  if (values.length === 0) return null
  return values.reduce((acc, v) => acc + v, 0) / values.length
}

/** Stored grade an evaluator gave for a single criterion (or null). */
function storedGrade(evaluation: IArtEvaluationByEvaluator | undefined, criterionId: number): number | null {
  if (!evaluation) return null
  const found = evaluation.criterion_grades.find((g) => g.contest_evaluation_criterion_id === criterionId)
  return found && found.grade !== null && found.grade !== undefined ? found.grade : null
}

/** Map of criterionId -> grade for one evaluator's stored grades. */
function gradesMap(evaluation: IArtEvaluationByEvaluator | undefined): Record<number, number | null> {
  const map: Record<number, number | null> = {}
  if (!evaluation) return map
  for (const g of evaluation.criterion_grades) map[g.contest_evaluation_criterion_id] = g.grade
  return map
}

function optionFromCriterion(criterion: IContestEvaluationCriterion) {
  return { value: String(criterion.id), label: `${criterion.name} · ${criterion.weight}%` }
}

export default function ArtworkEvaluationModal({
  isOpen,
  onClose,
  artwork,
  evaluators,
  criteria,
  onSaved,
  onPrev,
  onNext,
  positionLabel,
  onStatusChanged,
  readOnly = false
}: IArtworkEvaluationModalProps) {
  const service = useMemo(() => new ContestsService(), [])
  const toast = useAppToast()
  const imagePreview = useDisclosure()

  const [imageUrlOverride, setImageUrlOverride] = useState<string | null>(null)
  const [isDownloadingImage, setIsDownloadingImage] = useState(false)

  const [criterion, setCriterion] = useState<IContestEvaluationCriterion | null>(null)
  const [evaluations, setEvaluations] = useState<IArtEvaluationByEvaluator[]>([])
  const [pendingGrades, setPendingGrades] = useState<Record<number, number>>({})
  const [inputStrings, setInputStrings] = useState<Record<number, string>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

  const [exhibitionStatus, setExhibitionStatus] = useState<IContestArtworkForEvaluation['exhibition_status']>(
    artwork?.exhibition_status || 'pending'
  )

  /**
   * Reset criterion + pending grades when the modal opens for a NEW artwork.
   * We key on `artwork?.id` so the criterion only resets when the avaliador
   * navigates to a different artwork — saving via `onSaved` produces a new
   * `artwork` object reference for the same id, and that used to bounce the
   * user back to the first criterion every time (DEV-486).
   */
  useEffect(() => {
    if (!isOpen || !artwork) return
    setCriterion(criteria[0] ?? null)
    setPendingGrades({})
    setInputStrings({})
    setImageUrlOverride(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, artwork?.id])

  /**
   * Keep the in-modal `evaluations` snapshot fresh whenever the parent
   * updates the artwork (e.g. after saving a sibling criterion). This is
   * the single source for `evaluations` updates.
   */
  useEffect(() => {
    if (!isOpen || !artwork) return
    setEvaluations(artwork.evaluations)
  }, [isOpen, artwork])

  /**
   * Sync the local exhibition status with the prop when artwork changes.
   * This ensures the button displays the current status from the parent.
   */
  useEffect(() => {
    if (!isOpen || !artwork) return
    setExhibitionStatus(artwork.exhibition_status)
  }, [isOpen, artwork?.id, artwork?.exhibition_status])

  /**
   * DEV-489 — Arrow keys navigate prev/next when the modal is open and the
   * focus isn't trapped inside an input/slider.
   */
  useEffect(() => {
    if (!isOpen) return
    const isInteractive = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      (el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.tagName === 'SELECT' ||
        el.getAttribute('role') === 'slider')

    const handler = (event: KeyboardEvent) => {
      if (isInteractive(event.target)) return
      if (event.key === 'ArrowLeft' && onPrev) {
        event.preventDefault()
        onPrev()
      } else if (event.key === 'ArrowRight' && onNext) {
        event.preventDefault()
        onNext()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onPrev, onNext])

  const evaluatorRows = useMemo(() => {
    return evaluators.map((e) => {
      const existing = evaluations.find((ev) => ev.contest_evaluator_id === e.id)
      const stored = criterion ? storedGrade(existing, criterion.id) : null
      const pending = pendingGrades[e.id]
      const value = pending !== undefined ? pending : stored !== null ? stored : 0
      return { evaluator: e, value, hasStored: stored !== null }
    })
  }, [evaluators, evaluations, criterion, pendingGrades])

  const filledForCriterion = useMemo(
    () =>
      evaluators.reduce((acc, e) => {
        const found = evaluations.find((ev) => ev.contest_evaluator_id === e.id)
        const stored = criterion ? storedGrade(found, criterion.id) : null
        const pending = pendingGrades[e.id]
        if (pending !== undefined || stored !== null) return acc + 1
        return acc
      }, 0),
    [evaluators, evaluations, criterion, pendingGrades]
  )

  const criterionAverage = useMemo(() => {
    if (!criterion) return null
    const values = evaluators
      .map((e) => {
        const pending = pendingGrades[e.id]
        if (pending !== undefined) return pending
        const found = evaluations.find((ev) => ev.contest_evaluator_id === e.id)
        return storedGrade(found, criterion.id)
      })
      .filter((v): v is number => v !== null)

    return avg(values)
  }, [evaluators, evaluations, criterion, pendingGrades])

  const overallAverage = useMemo(() => {
    const perEvaluatorAverages = evaluators
      .map((e) => {
        const found = evaluations.find((ev) => ev.contest_evaluator_id === e.id)
        if (!found) return null
        return calculateWeightedAverage(gradesMap(found), criteria)
      })
      .filter((v): v is number => v !== null)

    return avg(perEvaluatorAverages)
  }, [evaluations, evaluators, criteria])

  const handleGradeChange = (evaluatorId: number, value: number) => {
    const clamped = Math.min(5, Math.max(0, Number(value) || 0))
    // Standardize to half-point increments.
    const rounded = Math.round(clamped * 2) / 2
    setPendingGrades((prev) => ({ ...prev, [evaluatorId]: rounded }))
  }

  const handleSave = async () => {
    if (!artwork || !criterion) return

    const grades = evaluatorRows
      .filter((row) => pendingGrades[row.evaluator.id] !== undefined)
      .map((row) => ({ evaluator_id: row.evaluator.id, grade: row.value }))

    if (grades.length === 0) {
      toast.info('Altere ao menos uma nota antes de salvar.')
      return
    }

    // Capture the criterion that we're about to save. We compare it with the
    // current `criterion` after the request resolves so we don't override a
    // manual criterion change made by the avaliador while the save is in
    // flight (DEV-486 review feedback).
    const savedCriterion = criterion

    setIsSaving(true)
    try {
      const result = await service.storeArtEvaluations(artwork.id, {
        criterion_id: savedCriterion.id,
        grades
      })
      setEvaluations(result.data)
      setPendingGrades({})

      // Advance to the next criterion only if the avaliador hasn't moved
      // away on their own while the save was in flight.
      const currentIndex = criteria.findIndex((option) => option.id === savedCriterion.id)
      const nextCriterion = currentIndex >= 0 ? criteria[currentIndex + 1] : undefined
      const shouldAdvance = !!nextCriterion && criterion?.id === savedCriterion.id
      if (shouldAdvance) {
        setCriterion(nextCriterion)
      }

      toast.success(
        shouldAdvance ? `Avaliação salva — avançando para ${nextCriterion!.name}` : 'Avaliação salva com sucesso'
      )
      onSaved(artwork.id, {
        ...artwork,
        evaluations: result.data,
        overall_average: result.overall_average,
        evaluation_status: result.evaluation_status
      })
    } catch {
      toast.error('Erro ao salvar avaliação')
    } finally {
      setIsSaving(false)
    }
  }

  const handleExhibitionStatusChange = async (status: 'approved' | 'rejected' | 'pending' | 'stand_by') => {
    if (!artwork) return
    setIsUpdatingStatus(true)
    try {
      await service.updateArtExhibitionStatus(artwork.id, status)
      setExhibitionStatus(status)
      toast.success('Status atualizado com sucesso!')
      onStatusChanged?.(artwork.id, status)
    } catch {
      toast.error('Erro ao atualizar status!')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  // ABRA-297 — clicar na imagem abre o lightbox, então o download não pode subir junto.
  const handleDownloadImage = async (event: MouseEvent) => {
    event.stopPropagation()
    if (!artwork) return
    setIsDownloadingImage(true)
    try {
      await service.downloadArtworkImage(artwork.id, `obra-${artwork.id}.jpg`)
    } catch (error) {
      toast.error('Erro ao baixar a imagem da obra', error)
    } finally {
      setIsDownloadingImage(false)
    }
  }

  if (!artwork) return null

  const criterionLabel = criterion?.name ?? 'critério'

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        size="wide"
        isDismissible={true}
        footer={
          <>
            <Flex gap="2" align="center">
              {onPrev && (
                <Tooltip label="Obra anterior (←)">
                  <Button
                    variant="outline"
                    onClick={onPrev}
                    leftIcon={<FiChevronLeft />}
                    fontSize="14px"
                    fontWeight="600"
                    color="gray.700"
                    borderColor="gray.200"
                    borderRadius="10px"
                    _hover={{ borderColor: 'magenta.500', color: 'magenta.500' }}
                  >
                    Voltar
                  </Button>
                </Tooltip>
              )}
              {onNext && (
                <Tooltip label="Próxima obra (→)">
                  <Button
                    variant="outline"
                    onClick={onNext}
                    rightIcon={<FiChevronRight />}
                    fontSize="14px"
                    fontWeight="600"
                    color="gray.700"
                    borderColor="gray.200"
                    borderRadius="10px"
                    _hover={{ borderColor: 'magenta.500', color: 'magenta.500' }}
                  >
                    Próxima
                  </Button>
                </Tooltip>
              )}
              {positionLabel && (
                <Text fontSize="12px" color="gray.500" fontWeight="500" ml="2">
                  {positionLabel}
                </Text>
              )}
            </Flex>

            <Flex gap="2">
              <Button
                variant="ghost"
                onClick={onClose}
                fontSize="14px"
                fontWeight="600"
                color="gray.600"
                borderRadius="10px"
                _hover={{ bg: 'gray.100' }}
              >
                Fechar
              </Button>
              {!readOnly && (
                <Button
                  bg="magenta.500"
                  color="white"
                  onClick={handleSave}
                  isLoading={isSaving}
                  loadingText="Salvando..."
                  isDisabled={Object.keys(pendingGrades).length === 0}
                  fontSize="14px"
                  fontWeight="600"
                  borderRadius="10px"
                  px="6"
                  _hover={{ bg: 'magenta.600' }}
                  _active={{ transform: 'scale(0.98)' }}
                  sx={{ transition: 'all .15s ease' }}
                >
                  Salvar critério
                </Button>
              )}
            </Flex>
          </>
        }
      >
        <Flex direction={{ base: 'column', md: 'row' }}>
          {/*
            LEFT — artwork. O contêiner da imagem inteiro é o controle de zoom
            (role="button"), então o botão de download mora fora dele, como
            irmão: um controle dentro de outro controle é uma árvore de
            acessibilidade inválida para o leitor de tela.
          */}
          <Box position="relative" display="flex" flex="1">
            <Box
              position="relative"
              flex="1"
              minH={{ base: '260px', md: '560px' }}
              bg="gray.100"
              cursor={artwork.art_image_url ? 'zoom-in' : 'default'}
              onClick={() => {
                if (artwork.art_image_url) imagePreview.onOpen()
              }}
              role={artwork.art_image_url ? 'button' : undefined}
              aria-label={artwork.art_image_url ? 'Abrir imagem em tamanho maior' : undefined}
              sx={{
                transition: 'all .2s ease',
                ':hover .abra-zoom-hint': { opacity: 1, transform: 'translateY(0)' },
                ':hover img': { transform: 'scale(1.02)' }
              }}
            >
              <Image
                src={artwork.art_image_url || ''}
                alt={artwork.name}
                w="100%"
                h="100%"
                objectFit="cover"
                position="absolute"
                inset="0"
                sx={{ transition: 'transform .4s ease' }}
                fallback={
                  <Flex
                    w="100%"
                    h="100%"
                    bg="gray.100"
                    align="center"
                    justify="center"
                    direction="column"
                    gap="2"
                    color="gray.400"
                  >
                    <Box fontSize="32px">
                      <FiImage />
                    </Box>
                    <Text fontSize="sm">Sem imagem</Text>
                  </Flex>
                }
              />
              <Box
                position="absolute"
                inset="0"
                bg="linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.75) 100%)"
                pointerEvents="none"
              />

              {artwork.art_image_url && (
                <Flex
                  className="abra-zoom-hint"
                  position="absolute"
                  top="12px"
                  left="12px"
                  align="center"
                  gap="1.5"
                  px="2.5"
                  py="1.5"
                  borderRadius="full"
                  bg="rgba(255,255,255,0.95)"
                  color="gray.800"
                  fontSize="11px"
                  fontWeight="600"
                  backdropFilter="blur(8px)"
                  boxShadow="0 2px 8px rgba(0,0,0,0.12)"
                  opacity="0"
                  pointerEvents="none"
                  sx={{ transition: 'all .2s ease', transform: 'translateY(-4px)' }}
                >
                  <FiMaximize2 size={12} />
                  Ampliar imagem
                </Flex>
              )}

              {/*
              Container do título: precisa receber pointer events para o
              Tooltip do nome funcionar. O click bubble continua subindo
              pro Box de zoom no parent (DEV-487 review).
            */}
              <Box position="absolute" bottom="0" left="0" right="0" p="5">
                <Box
                  display="inline-block"
                  px="2.5"
                  py="1"
                  borderRadius="full"
                  bg="rgba(255,255,255,0.2)"
                  backdropFilter="blur(8px)"
                  color="white"
                  fontSize="11px"
                  fontWeight="600"
                  mb="2"
                >
                  {readOnly ? 'Obra' : 'Obra em avaliação'}
                </Box>
                <Tooltip label={artwork.name} placement="top-start" bg="gray.800" color="white" borderRadius="6px">
                  <Text
                    fontSize={{ base: 'xl', md: '2xl' }}
                    fontWeight="700"
                    color="white"
                    lineHeight="1.2"
                    letterSpacing="-0.02em"
                    mb="0.5"
                  >
                    {artwork.name}
                  </Text>
                </Tooltip>
                <Text fontSize="sm" color="rgba(255,255,255,0.85)" noOfLines={1}>
                  {artwork.applicant_name || 'Artista desconhecido'}
                </Text>
                {resolveArtMeta(artwork).length > 0 && (
                  <ArtMetaInline
                    meta={resolveArtMeta(artwork)}
                    fontSize="xs"
                    color="rgba(255,255,255,0.7)"
                    noOfLines={1}
                    mt="1"
                  />
                )}
              </Box>
            </Box>

            {artwork.art_image_url && (
              <Box position="absolute" top="12px" right="12px">
                <ActionButton
                  action="download"
                  label="Baixar imagem"
                  tooltipPlacement="left"
                  onClick={handleDownloadImage}
                  isLoading={isDownloadingImage}
                  mr="0"
                  w="34px"
                  minW="34px"
                  h="34px"
                  borderRadius="full"
                  variant="solid"
                  colorScheme="gray"
                  bg="rgba(255,255,255,0.95)"
                  color="gray.700"
                  boxShadow="0 2px 8px rgba(0,0,0,0.12)"
                  backdropFilter="blur(8px)"
                  _hover={{ bg: 'white', color: 'magenta.500' }}
                />
              </Box>
            )}
          </Box>

          {/* RIGHT — form */}
          <Box flex="1" p={{ base: '5', md: '6' }}>
            <Flex align="center" justify="space-between" mb="4" gap="3">
              <Box>
                <Text fontSize="12px" fontWeight="500" color="gray.500" mb="0.5">
                  Média de {criterionLabel}
                </Text>
                <Flex align="baseline" gap="3">
                  <Text
                    fontSize="32px"
                    fontWeight="700"
                    color="gray.900"
                    lineHeight="1"
                    letterSpacing="-0.03em"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {criterionAverage !== null ? criterionAverage.toFixed(1) : '—'}
                    <Box as="span" fontSize="16px" color="gray.400" fontWeight="500">
                      /5
                    </Box>
                  </Text>
                  <Text fontSize="11px" color="gray.500" fontWeight="500">
                    Geral: {overallAverage !== null ? overallAverage.toFixed(1) : '—'}/5
                  </Text>
                </Flex>
              </Box>
              <Box
                px="3"
                py="1.5"
                borderRadius="full"
                bg="gray.100"
                color="gray.700"
                fontSize="11px"
                fontWeight="600"
                flexShrink={0}
              >
                {filledForCriterion} de {evaluators.length} avaliador{evaluators.length === 1 ? '' : 'es'}
              </Box>
            </Flex>

            {/* Segmented work type + concept description, shown in full for graders */}
            {(artwork.work_type || artwork.work_description) && (
              <Box mb="4" p="3" bg="gray.50" borderRadius="md">
                {artwork.work_type && (
                  <Flex gap="2" align="baseline">
                    <Text fontSize="12px" fontWeight="600" color="gray.600">
                      Tipo de Trabalho:
                    </Text>
                    <Text fontSize="13px" color="gray.800">
                      {artwork.work_type}
                    </Text>
                  </Flex>
                )}
                {artwork.work_description && (
                  <Box mt={artwork.work_type ? '2' : '0'}>
                    <Text fontSize="12px" fontWeight="600" color="gray.600" mb="1">
                      Descrição da Obra
                    </Text>
                    <Text fontSize="13px" color="gray.700" whiteSpace="pre-wrap">
                      {artwork.work_description}
                    </Text>
                  </Box>
                )}
              </Box>
            )}

            <Box mb="4">
              <Text fontSize="12px" fontWeight="500" color="gray.600" mb="2">
                Status da Exposição
              </Text>
              {readOnly ? (
                <StatusBadge status={exhibitionStatus} statuses={EXHIBITION_STATUS} />
              ) : (
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    size="sm"
                    variant="outline"
                    colorScheme={EXHIBITION_STATUS[exhibitionStatus]?.colorScheme ?? 'yellow'}
                    width="100%"
                    textAlign="left"
                    justifyContent="space-between"
                    fontWeight="500"
                    fontSize="14px"
                    isLoading={isUpdatingStatus}
                    disabled={isUpdatingStatus}
                  >
                    {EXHIBITION_STATUS[exhibitionStatus]?.label ?? 'Desconhecido'}
                  </MenuButton>
                  <MenuList minW="100%" py={1}>
                    <MenuItem
                      icon={<i className="lni lni-checkmark" style={{ fontSize: '14px' }} />}
                      onClick={() => handleExhibitionStatusChange('approved')}
                      fontSize="sm"
                      fontWeight="medium"
                      color="green.600"
                      py={2.5}
                      px={4}
                      _hover={{ bg: 'green.50' }}
                    >
                      Aprovar
                    </MenuItem>
                    <MenuItem
                      icon={<i className="lni lni-reload" style={{ fontSize: '14px' }} />}
                      onClick={() => handleExhibitionStatusChange('pending')}
                      fontSize="sm"
                      fontWeight="medium"
                      color="yellow.600"
                      py={2.5}
                      px={4}
                      _hover={{ bg: 'yellow.50' }}
                    >
                      Pendente
                    </MenuItem>
                    <MenuItem
                      icon={<i className="lni lni-pause" style={{ fontSize: '14px' }} />}
                      onClick={() => handleExhibitionStatusChange('stand_by')}
                      fontSize="sm"
                      fontWeight="medium"
                      color="orange.600"
                      py={2.5}
                      px={4}
                      _hover={{ bg: 'orange.50' }}
                    >
                      Stand-by
                    </MenuItem>
                    <MenuItem
                      icon={<i className="lni lni-close" style={{ fontSize: '14px' }} />}
                      onClick={() => handleExhibitionStatusChange('rejected')}
                      fontSize="sm"
                      fontWeight="medium"
                      color="red.600"
                      py={2.5}
                      px={4}
                      _hover={{ bg: 'red.50' }}
                    >
                      Reprovar
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </Box>

            <Box mb="4">
              <Text fontSize="12px" fontWeight="500" color="gray.600" mb="1.5">
                {readOnly ? 'Critério' : 'Critério para avaliar'}
              </Text>
              <FormSelect
                options={criteria.map(optionFromCriterion)}
                value={criterion ? String(criterion.id) : ''}
                onChange={(criterionId) => {
                  const next = criteria.find((item) => String(item.id) === criterionId)
                  if (next) setCriterion(next)
                }}
              />
            </Box>

            <VStack spacing="3" align="stretch" maxH="320px" overflowY="auto" pr="1">
              {evaluatorRows.length === 0 && <Text variant="caption">Nenhum avaliador cadastrado neste concurso.</Text>}
              {evaluatorRows.map((row) => (
                <Box key={row.evaluator.id}>
                  <Flex justify="space-between" align="center" mb="1.5">
                    <Tooltip
                      label={`Nota de ${row.evaluator.name} para ${criterionLabel}`}
                      bg="gray.800"
                      color="white"
                      borderRadius="6px"
                    >
                      <Text fontSize="13px" fontWeight="600" color="gray.800" cursor="help" noOfLines={1}>
                        {row.evaluator.name}
                      </Text>
                    </Tooltip>
                    <Text fontSize="11px" color={row.hasStored ? 'gray.500' : 'orange.500'} fontWeight="500">
                      {row.hasStored ? 'salvo' : 'não avaliado'}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="3">
                    <Input
                      isReadOnly={readOnly}
                      value={
                        readOnly && !row.hasStored
                          ? '—'
                          : (inputStrings[row.evaluator.id] ?? String(row.value).replace('.', ','))
                      }
                      onChange={(e) => {
                        const strVal = e.target.value
                        setInputStrings((prev) => ({ ...prev, [row.evaluator.id]: strVal }))
                        const parsed = parseFloat(strVal.replace(',', '.'))
                        if (!isNaN(parsed)) {
                          handleGradeChange(row.evaluator.id, parsed)
                        }
                      }}
                      onBlur={() => {
                        const str = inputStrings[row.evaluator.id]
                        if (str !== undefined) {
                          const parsed = parseFloat(str.replace(',', '.'))
                          if (!isNaN(parsed)) handleGradeChange(row.evaluator.id, parsed)
                          setInputStrings((prev) => {
                            const next = { ...prev }
                            delete next[row.evaluator.id]
                            return next
                          })
                        }
                      }}
                      textAlign="center"
                      fontSize="14px"
                      fontWeight="600"
                      borderRadius="8px"
                      bg={readOnly ? 'gray.50' : 'white'}
                      borderColor="gray.200"
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{
                        borderColor: 'magenta.500',
                        boxShadow: '0 0 0 3px rgba(250,0,105,0.12)'
                      }}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                      size="sm"
                      w="80px"
                      flexShrink={0}
                    />
                    <Slider
                      isReadOnly={readOnly}
                      value={row.value}
                      onChange={(val) => {
                        handleGradeChange(row.evaluator.id, val)
                        setInputStrings((prev) => {
                          const next = { ...prev }
                          delete next[row.evaluator.id]
                          return next
                        })
                      }}
                      min={0}
                      max={5}
                      step={0.5}
                      flex="1"
                      focusThumbOnChange={false}
                    >
                      <SliderTrack bg="gray.100" h="5px" borderRadius="full">
                        <SliderFilledTrack bg="magenta.500" />
                      </SliderTrack>
                      <SliderThumb
                        boxSize="16px"
                        bg="white"
                        border="2px solid"
                        borderColor="magenta.500"
                        boxShadow="0 2px 6px rgba(0,0,0,0.12)"
                        _focus={{ boxShadow: '0 0 0 4px rgba(250,0,105,0.18)' }}
                      />
                    </Slider>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </Box>
        </Flex>
      </FormModal>
      <ArtworkPreviewModal
        isOpen={imagePreview.isOpen}
        onClose={imagePreview.onClose}
        imageUrl={imageUrlOverride ?? artwork.art_image_url}
        imageAlt={artwork.name}
        artworkId={readOnly ? undefined : artwork.id}
        onRotationSaved={readOnly ? undefined : (newUrl) => setImageUrlOverride(newUrl)}
        onCropSaved={readOnly ? undefined : (newUrl) => setImageUrlOverride(newUrl)}
        onPrev={onPrev}
        onNext={onNext}
        positionLabel={positionLabel}
      />
    </>
  )
}
